import { useState, useEffect } from 'react';
import { usuariosApi } from '../../utils/api';
import { Shield, Plus, Pencil, Delete, X, Check, Users, ChevronDown, ChevronRight, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const MODULE_LABELS = {
  facturacion: 'Facturación',
  clientes: 'Clientes',
  productos: 'Productos',
  inventarios: 'Inventarios',
  cotizaciones: 'Cotizaciones',
  gastos: 'Gastos',
  cxc: 'Cuentas por Cobrar',
  cxp: 'Cuentas por Pagar',
  planillas: 'Planillas (Payroll360 Lite)',
  configuracion: 'Configuración',
  respaldos: 'Respaldos',
  reportes: 'Reportes',
  contabilidad: 'Contabilidad'
};

const SUBMODULE_LABELS = {
  nueva: 'Nueva Factura',
  historial: 'Historial',
  respaldos: 'Respaldos',
  notas_credito: 'Notas de Crédito',
  lista: 'Lista',
  importar: 'Importar',
  bandeja: 'Bandeja Email',
  por_confirmar: 'Por Confirmar',
  aceptadas: 'Aceptadas',
  iva: 'Control IVA',
  fec: 'FEC',
  pendientes: 'Pendientes',
  historico: 'Histórico',
  facturas: 'Facturas',
  proveedores: 'Proveedores',
  empresas: 'Empresas',
  parametros: 'Parámetros',
  usuarios: 'Usuarios',
  roles: 'Roles',
  centro: 'Centro de Reportes',
  dashboard: 'Dashboard',
  cuentas: 'Plan de Cuentas',
  asientos: 'Asientos',
  mayor: 'Libro Mayor',
  estados: 'Estados Financieros',
  bancos: 'Bancos',
  config: 'Configuración',
  modulo: 'Módulo'
};

const ACTION_LABELS = {
  ver: 'Ver',
  crear: 'Crear',
  editar: 'Editar',
  eliminar: 'Eliminar',
  anular: 'Anular',
  reenviar: 'Reenviar',
  descargar: 'Descargar',
  ejecutar: 'Ejecutar',
  importar: 'Importar',
  aceptar: 'Aceptar',
  rechazar: 'Rechazar',
  cerrar_periodo: 'Cerrar Período',
  cobrar: 'Cobrar',
  pagar: 'Pagar',
  reversar: 'Reversar',
  contabilizar: 'Contabilizar',
  conciliar: 'Conciliar',
  exportar: 'Exportar',
  corregir_descripcion: 'Corregir descripción',
  restaurar: 'Restaurar'
};

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState({ list: [], grouped: {} });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRol, setEditingRol] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permisosRes] = await Promise.all([
        usuariosApi.listRoles(),
        usuariosApi.listPermisos()
      ]);
      setRoles(rolesRes.data.data || []);
      setPermisos(permisosRes.data.data || { list: [], grouped: {} });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rol) => {
    if (rol.is_system) {
      toast.error('No se pueden eliminar roles del sistema');
      return;
    }
    if (!confirm(`¿Está seguro de eliminar el rol "${rol.nombre}"?`)) return;

    try {
      await usuariosApi.deleteRol(rol.id);
      toast.success('Rol eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  const openNew = () => {
    setEditingRol(null);
    setShowModal(true);
  };

  const openEdit = async (rol) => {
    try {
      const res = await usuariosApi.getRol(rol.id);
      setEditingRol(res.data.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Error al cargar rol');
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingRol) {
        await usuariosApi.updateRol(editingRol.id, formData);
        toast.success('Rol actualizado');
      } else {
        await usuariosApi.createRol(formData);
        toast.success('Rol creado');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Plus size={18} />
          Nuevo Rol
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : roles.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay roles registrados.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Rol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Descripción</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Usuarios</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {roles.map((rol) => (
                <tr key={rol.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {rol.is_system && <Lock size={14} className="text-gray-400" title="Rol del sistema" />}
                      <span className="font-medium text-gray-900">{rol.nombre}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {rol.descripcion || '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                      <Users size={12} />
                      {rol.usuarios_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={clsx(
                      'px-2 py-1 rounded text-xs font-medium',
                      rol.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    )}>
                      {rol.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEdit(rol)}
                        className="p-1.5 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Pencil className="h-5 w-5 text-blue-500" />
                      </button>
                      {!rol.is_system && (
                        <button
                          onClick={() => handleDelete(rol)}
                          className="p-1.5 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Delete className="h-5 w-5 text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <RolModal
          rol={editingRol}
          permisos={permisos}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// Rol Modal Component with Permission Checkboxes
function RolModal({ rol, permisos, onClose, onSave }) {
  const [formData, setFormData] = useState({
    nombre: rol?.nombre || '',
    descripcion: rol?.descripcion || '',
    is_active: rol?.is_active !== false,
    permiso_ids: rol?.permiso_ids || []
  });
  const [expandedModules, setExpandedModules] = useState(
    Object.keys(permisos.grouped || {})
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (modulo) => {
    setExpandedModules(prev =>
      prev.includes(modulo)
        ? prev.filter(m => m !== modulo)
        : [...prev, modulo]
    );
  };

  const togglePermiso = (permisoId) => {
    setFormData(prev => ({
      ...prev,
      permiso_ids: prev.permiso_ids.includes(permisoId)
        ? prev.permiso_ids.filter(id => id !== permisoId)
        : [...prev.permiso_ids, permisoId]
    }));
  };

  const toggleAllInSubmodule = (submoduloPermisos) => {
    const ids = submoduloPermisos.map(p => p.id);
    const allSelected = ids.every(id => formData.permiso_ids.includes(id));

    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        permiso_ids: prev.permiso_ids.filter(id => !ids.includes(id))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permiso_ids: [...new Set([...prev.permiso_ids, ...ids])]
      }));
    }
  };

  const toggleAllInModule = (modulo) => {
    const submodulos = permisos.grouped[modulo] || {};
    const allIds = Object.values(submodulos).flat().map(p => p.id);
    const allSelected = allIds.every(id => formData.permiso_ids.includes(id));

    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        permiso_ids: prev.permiso_ids.filter(id => !allIds.includes(id))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permiso_ids: [...new Set([...prev.permiso_ids, ...allIds])]
      }));
    }
  };

  const isModuleFullySelected = (modulo) => {
    const submodulos = permisos.grouped[modulo] || {};
    const allIds = Object.values(submodulos).flat().map(p => p.id);
    return allIds.length > 0 && allIds.every(id => formData.permiso_ids.includes(id));
  };

  const isModulePartiallySelected = (modulo) => {
    const submodulos = permisos.grouped[modulo] || {};
    const allIds = Object.values(submodulos).flat().map(p => p.id);
    const selectedCount = allIds.filter(id => formData.permiso_ids.includes(id)).length;
    return selectedCount > 0 && selectedCount < allIds.length;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl mx-4 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">{rol ? 'Editar Rol' : 'Nuevo Rol'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          <div className="p-6 space-y-4 border-b">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={rol?.is_system}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <input
                type="text"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Permissions Section */}
          <div className="flex-1 overflow-y-auto p-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Permisos por Módulo</h4>

            <div className="space-y-2">
              {Object.entries(permisos.grouped || {}).map(([modulo, submodulos]) => (
                <div key={modulo} className="border rounded-lg overflow-hidden">
                  {/* Module Header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleModule(modulo)}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isModuleFullySelected(modulo)}
                        ref={el => {
                          if (el) el.indeterminate = isModulePartiallySelected(modulo);
                        }}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleAllInModule(modulo);
                        }}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="font-medium text-gray-900">
                        {MODULE_LABELS[modulo] || modulo}
                      </span>
                    </div>
                    {expandedModules.includes(modulo) ? (
                      <ChevronDown size={18} className="text-gray-400" />
                    ) : (
                      <ChevronRight size={18} className="text-gray-400" />
                    )}
                  </div>

                  {/* Submodules */}
                  {expandedModules.includes(modulo) && (
                    <div className="divide-y">
                      {Object.entries(submodulos).map(([submodulo, acciones]) => (
                        <div key={submodulo} className="px-4 py-3 pl-8">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              checked={acciones.every(p => formData.permiso_ids.includes(p.id))}
                              ref={el => {
                                if (el) {
                                  const selectedCount = acciones.filter(p => formData.permiso_ids.includes(p.id)).length;
                                  el.indeterminate = selectedCount > 0 && selectedCount < acciones.length;
                                }
                              }}
                              onChange={() => toggleAllInSubmodule(acciones)}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              {SUBMODULE_LABELS[submodulo] || submodulo}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 pl-6">
                            {acciones.map((permiso) => (
                              <label
                                key={permiso.id}
                                className={clsx(
                                  'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition-colors',
                                  formData.permiso_ids.includes(permiso.id)
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.permiso_ids.includes(permiso.id)}
                                  onChange={() => togglePermiso(permiso.id)}
                                  className="sr-only"
                                />
                                {ACTION_LABELS[permiso.accion] || permiso.accion}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center gap-3 p-4 border-t bg-gray-50">
            <span className="text-sm text-gray-500">
              {formData.permiso_ids.length} permisos seleccionados
            </span>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
